import { CHAT_MAX_LENGTH, type ChatMessage, type RoomChatState } from "../worker/src/room-chat";
import "./room-chat.css";

export const roomChatButtonHtml = (creation = false) => `<button id="${creation ? "sg-chat-button" : "room-chat-button"}" data-room-chat-button class="room-chat-button${creation ? " sg-chat-button" : ""}" type="button" aria-label="${creation ? "Chat together" : "Open room chat"}" aria-controls="room-chat-panel" aria-expanded="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h16v12H9l-5 4V4Z"/><path d="M8 8h8M8 12h5"/></svg>${creation ? "<span>Chat together</span>" : ""}<span class="room-chat-badge" hidden></span></button>`;

export class RoomChat {
  private root = document.createElement("div");
  private panel: HTMLElement;
  private input: HTMLInputElement;
  private list: HTMLElement;
  private notice: HTMLElement;
  private status: HTMLElement;
  private sendButton: HTMLButtonElement;
  private clearButton: HTMLButtonElement;
  private clearConfirm: HTMLElement;
  private clearPending: { clientId: string; timer: number } | null = null;
  private state: RoomChatState | null = null;
  private roomCode = "";
  private connected = false;
  private opened = false;
  private unread = new Set<string>();
  private seen = new Set<string>();
  private boundButtons = new WeakSet<HTMLElement>();
  private dock: HTMLElement | null = null;
  private opener: HTMLElement | null = null;
  private pending: { clientId: string; text: string; timer: number } | null = null;
  private retry: { clientId: string; text: string } | null = null;

  constructor(private playerId: () => string, private send: (message: object) => boolean) {
    this.root.className = "room-chat-root";
    this.root.innerHTML = `<section id="room-chat-panel" class="room-chat-panel" role="dialog" aria-labelledby="room-chat-title" hidden>
      <header class="room-chat-head"><div class="room-chat-heading"><h2 id="room-chat-title">Room chat</h2><p>Make a plan for your next game</p></div><div class="room-chat-head-actions"><button class="room-chat-clear" type="button">Clear chat</button><button class="room-chat-close" type="button" aria-label="Close room chat">×</button></div></header>
      <div class="room-chat-clear-confirm" role="group" aria-labelledby="room-chat-clear-question" hidden><p id="room-chat-clear-question">Clear all previous messages for everyone in this room?</p><div><button class="room-chat-cancel-clear" type="button">Keep messages</button><button class="room-chat-confirm-clear" type="button">Clear all messages</button></div></div>
      <div class="room-chat-messages" role="log" aria-label="Room messages" aria-live="polite" aria-relevant="additions"></div>
      <form class="room-chat-compose"><label for="room-chat-input">Message your party</label><div><input id="room-chat-input" class="room-chat-input" type="text" maxlength="${CHAT_MAX_LENGTH}" autocomplete="off" enterkeyhint="send" placeholder="Ready for the next game?"><button class="room-chat-send" type="submit">Send</button></div><p class="room-chat-status" role="status"></p></form>
      </section><aside class="room-chat-notice" aria-label="New room message" hidden><div class="room-chat-notice-copy"><strong></strong><p></p></div><button class="room-chat-reply" type="button">Reply</button><button class="room-chat-close" type="button" aria-label="Dismiss chat notification">×</button></aside><div class="room-chat-announcement" role="status" aria-live="polite"></div>`;
    document.body.append(this.root);
    this.panel = this.root.querySelector(".room-chat-panel")!;
    this.input = this.root.querySelector("input")!;
    this.list = this.root.querySelector(".room-chat-messages")!;
    this.notice = this.root.querySelector(".room-chat-notice")!;
    this.status = this.root.querySelector(".room-chat-status")!;
    this.sendButton = this.root.querySelector(".room-chat-send")!;
    this.clearButton = this.root.querySelector(".room-chat-clear")!;
    this.clearConfirm = this.root.querySelector(".room-chat-clear-confirm")!;
    this.clearButton.addEventListener("click", () => {
      this.clearConfirm.hidden = false;
      this.clearConfirm.querySelector<HTMLButtonElement>(".room-chat-cancel-clear")!.focus();
    });
    this.clearConfirm.querySelector(".room-chat-cancel-clear")!.addEventListener("click", () => {
      this.clearConfirm.hidden = true;
      this.clearButton.focus();
    });
    this.clearConfirm.querySelector(".room-chat-confirm-clear")!.addEventListener("click", () => this.clearMessages());
    this.panel.querySelector(".room-chat-close")!.addEventListener("click", () => this.close());
    this.notice.querySelector(".room-chat-close")!.addEventListener("click", () => { this.notice.hidden = true; });
    this.notice.querySelector(".room-chat-reply")!.addEventListener("click", () => this.open());
    this.root.querySelector("form")!.addEventListener("submit", (event) => { event.preventDefault(); this.submit(); });
    this.input.addEventListener("input", () => this.updateControls());
    this.input.addEventListener("keydown", (event) => { if (event.isComposing && event.key === "Enter") event.preventDefault(); });
    document.addEventListener("keydown", (event) => {
      if (this.opened && event.key === "Escape" && (!this.dock || this.panel.contains(document.activeElement))) {
        event.preventDefault();
        if (!this.clearConfirm.hidden) { this.clearConfirm.hidden = true; this.clearButton.focus(); }
        else this.close();
      }
    });
    window.visualViewport?.addEventListener("resize", () => this.fitViewport());
    window.visualViewport?.addEventListener("scroll", () => this.fitViewport());
  }

  setRoom(code: string | null) {
    if ((code || "") === this.roomCode) return;
    this.reset();
    this.roomCode = code || "";
  }

  bindButton() {
    document.querySelectorAll<HTMLElement>("[data-room-chat-button]").forEach(button => {
      if (this.boundButtons.has(button)) return;
      this.boundButtons.add(button);
      button.addEventListener("click", () => {
        this.opener = button;
        // A creation shortcut brings an already visible panel into view.
        if (this.dock) this.open();
        else this.opened ? this.close() : this.open();
      });
    });
    this.updateBadge();
  }

  attachToCreation(host: HTMLElement): () => void {
    this.dock = host;
    this.root.classList.add("room-chat-root--creation");
    host.append(this.root);
    this.panel.style.removeProperty("top");
    this.panel.style.removeProperty("max-height");
    this.setCreationCopy(true);
    this.bindButton();
    this.open(false);
    return () => {
      if (this.dock !== host) return;
      this.close(false);
      this.dock = null;
      this.opener = null;
      this.root.classList.remove("room-chat-root--creation");
      this.setCreationCopy(false);
      document.body.append(this.root);
      this.fitViewport();
    };
  }

  private setCreationCopy(creation: boolean) {
    this.panel.setAttribute("role", creation ? "region" : "dialog");
    this.panel.querySelector("h2")!.textContent = creation ? "Chat together" : "Room chat";
    this.panel.querySelector(".room-chat-head p")!.textContent = creation ? "Share ideas for our window" : "Make a plan for your next game";
    this.panel.querySelector("label")!.textContent = creation ? "Message your fellow makers" : "Message your party";
    this.panel.querySelector(".room-chat-close")!.setAttribute("aria-label", creation ? "Hide creation chat" : "Close room chat");
    this.input.placeholder = creation ? "Let’s make a sunrise…" : "Ready for the next game?";
    const empty = this.list.querySelector(".room-chat-empty");
    if (empty) empty.textContent = this.emptyCopy();
  }

  private emptyCopy() {
    return this.dock ? "What shall we make? Share a color, a pattern, or an idea with everyone in this room. You can chat anytime."
      : "Say hello, check who’s ready, or pick your next game together. Messages are shared with players in this room.";
  }

  setConnected(connected: boolean) {
    this.connected = connected;
    this.status.textContent = connected ? "" : "Reconnecting… Your draft is kept.";
    this.updateControls();
  }

  receive(code: string, next: RoomChatState) {
    if (code !== this.roomCode) return;
    const initial = !this.state || this.state.sessionId !== next.sessionId;
    if (this.state && initial) {
      const wasOpened = this.opened;
      const draft = this.input.value;
      this.reset();
      this.roomCode = code;
      this.input.value = draft;
      this.status.textContent = "Chat cleared for this room.";
      if (wasOpened) this.open(false);
    }
    const incoming = initial ? [] : next.messages.filter((message) => !this.seen.has(message.id) && message.playerId !== this.playerId());
    this.state = next;
    next.messages.forEach((message) => this.seen.add(message.id));
    if (this.pending && next.messages.some((message) => message.playerId === this.playerId() && message.clientId === this.pending!.clientId)) {
      clearTimeout(this.pending.timer);
      if (this.input.value.trim() === this.pending.text) this.input.value = "";
      this.pending = null;
      this.retry = null;
      this.status.textContent = "Sent";
    }
    this.renderMessages();
    if (incoming.length && !this.opened) {
      incoming.forEach((message) => this.unread.add(message.id));
      const last = incoming.at(-1)!;
      this.notice.querySelector("strong")!.textContent = `${last.playerName} · Room chat`;
      this.notice.querySelector("p")!.textContent = last.text;
      this.notice.hidden = false;
      this.root.querySelector(".room-chat-announcement")!.textContent = `New room message from ${last.playerName}. Open chat to reply.`;
    }
    this.updateBadge();
    this.updateControls();
  }

  error(clientId: unknown, message: string) {
    if (this.clearPending && this.clearPending.clientId === clientId) {
      clearTimeout(this.clearPending.timer);
      this.clearPending = null;
    }
    if (this.pending && this.pending.clientId === clientId) {
      clearTimeout(this.pending.timer);
      this.retry = { clientId: this.pending.clientId, text: this.pending.text };
      this.pending = null;
    }
    this.status.textContent = message;
    this.updateControls();
  }

  private open(focus = true) {
    if (!this.roomCode) return;
    this.opened = true;
    this.panel.hidden = false;
    this.notice.hidden = true;
    this.unread.clear();
    this.updateBadge();
    this.fitViewport();
    this.renderMessages();
    this.list.scrollTop = this.list.scrollHeight;
    if (focus) {
      if (this.dock) this.panel.scrollIntoView({ block: "nearest" });
      // Keep this synchronous inside the tap/click so mobile browsers can open their keyboard.
      this.input.focus({ preventScroll: true });
    }
  }

  private close(focus = true) {
    this.opened = false;
    this.panel.hidden = true;
    this.clearConfirm.hidden = true;
    this.input.blur();
    this.updateBadge();
    if (focus) (this.opener?.isConnected ? this.opener : document.querySelector<HTMLElement>(this.dock ? "#sg-chat-button" : "#room-chat-button"))?.focus({ preventScroll: true });
  }

  private submit() {
    const text = this.input.value.trim();
    if (!text || this.pending || this.clearPending || !this.connected || !this.state) return;
    const clientId = this.retry?.text === text ? this.retry.clientId : crypto.randomUUID();
    const timer = window.setTimeout(() => this.error(clientId, "Delivery unconfirmed. Your draft is kept; try Send again."), 10_000);
    this.pending = { clientId, text, timer };
    this.status.textContent = "Sending…";
    if (!this.send({ type: "chat_send", text, clientId, sessionId: this.state.sessionId })) this.error(clientId, "Reconnecting… Your draft is kept.");
    this.updateControls();
  }

  private clearMessages() {
    if (!this.connected || !this.state || this.pending || this.clearPending || !this.state.messages.length) return;
    const clientId = crypto.randomUUID();
    const timer = window.setTimeout(() => this.error(clientId, "Clearing unconfirmed. Reconnect before trying again."), 10_000);
    this.clearPending = { clientId, timer };
    this.clearConfirm.hidden = true;
    this.status.textContent = "Clearing chat…";
    if (!this.send({ type: "chat_clear", clientId, sessionId: this.state.sessionId })) this.error(clientId, "Reconnect to clear chat.");
    this.updateControls();
    this.input.focus({ preventScroll: true });
  }

  private renderMessages() {
    if (!this.state) return;
    const nearBottom = this.list.scrollHeight - this.list.scrollTop - this.list.clientHeight < 50;
    const oldIds = new Set(Array.from(this.list.querySelectorAll<HTMLElement>("[data-message-id]"), (node) => node.dataset.messageId));
    const keepIds = new Set(this.state.messages.map((message) => message.id));
    this.list.querySelectorAll<HTMLElement>("[data-message-id]").forEach((node) => { if (!keepIds.has(node.dataset.messageId!)) node.remove(); });
    if (this.state.messages.length) this.list.querySelector(".room-chat-empty")?.remove();
    for (const message of this.state.messages) {
      if (oldIds.has(message.id)) continue;
      this.list.append(this.messageNode(message));
    }
    if (!this.state.messages.length && !this.list.childElementCount) {
      const empty = document.createElement("p");
      empty.className = "room-chat-empty";
      empty.textContent = this.emptyCopy();
      this.list.append(empty);
    }
    if (nearBottom || !this.opened) this.list.scrollTop = this.list.scrollHeight;
  }

  private messageNode(message: ChatMessage) {
    const article = document.createElement("article");
    article.className = `room-chat-message${message.playerId === this.playerId() ? " is-mine" : ""}`;
    article.dataset.messageId = message.id;
    const name = document.createElement("small");
    name.textContent = message.playerId === this.playerId() ? `${message.playerName} (you)` : message.playerName;
    const text = document.createElement("p");
    text.textContent = message.text;
    article.append(name, text);
    return article;
  }

  private updateBadge() {
    document.querySelectorAll<HTMLElement>("[data-room-chat-button]").forEach(button => {
      button.setAttribute("aria-expanded", String(this.opened));
      const label = button.id === "sg-chat-button" ? "Chat together" : "Open room chat";
      button.setAttribute("aria-label", this.unread.size ? `${label}, ${this.unread.size} unread messages` : label);
      const badge = button.querySelector<HTMLElement>(".room-chat-badge");
      if (badge) { badge.hidden = !this.unread.size; badge.textContent = this.unread.size > 99 ? "99+" : String(this.unread.size); }
    });
  }

  private updateControls() {
    const busy = Boolean(this.pending || this.clearPending);
    this.sendButton.disabled = !this.connected || !this.state || busy || !this.input.value.trim();
    this.clearButton.disabled = !this.connected || !this.state?.messages.length || busy;
    this.clearConfirm.querySelector<HTMLButtonElement>(".room-chat-confirm-clear")!.disabled = this.clearButton.disabled;
  }

  private fitViewport() {
    const viewport = window.visualViewport;
    if (!viewport || this.dock) return;
    this.panel.style.top = `${viewport.offsetTop + 8}px`;
    this.panel.style.maxHeight = `${Math.max(100, viewport.height - 16)}px`;
  }

  private reset() {
    if (this.pending) clearTimeout(this.pending.timer);
    if (this.clearPending) clearTimeout(this.clearPending.timer);
    this.clearPending = null;
    this.clearConfirm.hidden = true;
    this.pending = null;
    this.retry = null;
    this.state = null;
    this.opened = false;
    this.panel.hidden = true;
    this.notice.hidden = true;
    this.unread.clear();
    this.seen.clear();
    this.input.value = "";
    this.list.replaceChildren();
    this.status.textContent = "";
    this.updateBadge();
    this.updateControls();
  }
}
